import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    createHelpSos,
    deleteHelpSos,
    getHelpSos,
    updateHelpSos,
} from "../../redux/helpSos/helpSosSlice";
import { getUsers } from "../../redux/userSpeaker/userSpeakerSlice";
import Select from "react-select";
import { toast } from "react-toastify";
import { truncateText } from "../../helper/helper";
import useDebounce from "../../hook/useDebounce";
import PaginationHelper from "../../helper/PaginationHelper";

const TITLE_MAX = 255;
const DESC_MAX = 255;

const HelpSosLayer = () => {
    const dispatch = useDispatch();

    const { list = [], total = 0 } = useSelector((state) => state.helpSos);
    const { users = [] } = useSelector((state) => state.speaker);

    const [entries, setEntries] = useState(50);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search, 1000);

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        userIds: [],
    });

    const [editId, setEditId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    useEffect(() => {
        dispatch(getHelpSos({ page, limit: entries, search: debouncedSearch }));
    }, [page, entries, debouncedSearch]);

    const totalPages = Math.ceil(total / entries);

    const userOptions = users.map((u) => ({
        value: u.id,
        label: u.fullName,
    }));

    const handleSubmit = () => {
        const { title, description, userIds } = formData;

        if (!title || !title.trim()) {
            toast.error("Title is required");
            return;
        }

        if (title.length > TITLE_MAX) {
            toast.error(`Title max ${TITLE_MAX} characters`);
            return;
        }

        if (description && description.length > DESC_MAX) {
            toast.error(`Description max ${DESC_MAX} characters`);
            return;
        }

        if (!userIds || userIds.length === 0) {
            toast.error("At least one user must be selected");
            return;
        }

        const action = editId
            ? updateHelpSos({ id: editId, data: formData })
            : createHelpSos(formData);

        dispatch(action)
            .unwrap()
            .then((res) => {
                const message =
                    res?.message ||
                    res?.data?.message ||
                    (editId ? "Updated successfully" : "Created successfully");

                toast.success(message);

                dispatch(getHelpSos({
                    page,
                    limit: entries,
                    search: debouncedSearch
                }));

                setFormData({ title: "", description: "", userIds: [] });
                setEditId(null);

                document.querySelector("#helpSosModal .btn-close")?.click();
            })
            .catch((err) => {
                const message =
                    err?.response?.data?.message ||
                    err?.message ||
                    "Something went wrong";

                toast.error(message);
            });
    };

    return (
        <>
            <div className="card h-100 p-0 radius-12">

                {/* HEADER */}
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between flex-wrap gap-3">

                    <div className="d-flex align-items-center gap-3 flex-wrap">

                        <span className="text-md fw-medium text-secondary-light">
                            Show
                        </span>

                        <select
                            className="form-select form-select-sm w-auto"
                            value={entries}
                            onChange={(e) => {
                                setEntries(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="150">150</option>
                            <option value="200">200</option>
                        </select>

                        {/* SEARCH */}
                        <input
                            type="text"
                            className="form-control w-auto"
                            placeholder="Search SOS..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                        />

                    </div>

                    <button
                        className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                        data-bs-toggle="modal"
                        data-bs-target="#helpSosModal"
                        onClick={() => {
                            dispatch(getUsers());

                            setEditId(null);
                            setFormData({ title: "", description: "", userIds: [] });
                        }}
                    >
                        <Icon icon="ic:baseline-plus" />
                        Add SOS
                    </button>

                </div>

                {/* TABLE */}
                <div className="card-body p-12">

                    <div className="table-responsive">

                        <table className="table bordered-table">

                            <thead>
                                <tr>
                                    <th>S.L</th>
                                    <th>Title</th>
                                    <th>Description</th>
                                    <th>Users</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>

                            <tbody>

                                {list.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center">
                                            No SOS found
                                        </td>
                                    </tr>
                                )}

                                {list.map((item, index) => (
                                    <tr key={item.id}>

                                        <td>
                                            {(page - 1) * entries + index + 1}
                                        </td>

                                        <td>
                                            {truncateText(item.title) || "N/A"}
                                        </td>
                                        <td>
                                            {truncateText(item.description) || "N/A"}
                                        </td>

                                        {/* USERS CHIPS */}
                                        <td>
                                            <div className="d-flex flex-wrap gap-2">

                                                {item.users?.map((u) => (
                                                    <span
                                                        key={u.id}
                                                        className="px-10 py-4 bg-success-100 text-success-600 radius-8 text-sm"
                                                    >
                                                        {u.fullName}
                                                    </span>
                                                ))}

                                            </div>
                                        </td>

                                        {/* ACTION */}
                                        <td className="text-center">

                                            <div className="d-flex justify-content-center gap-2">

                                                <button
                                                    className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    data-bs-toggle="modal"
                                                    data-bs-target="#helpSosModal"
                                                    onClick={() => {
                                                        dispatch(getUsers());

                                                        setEditId(item.id);
                                                        setFormData({
                                                            title: item.title,
                                                            description: item.description,
                                                            userIds: item.users?.map((u) => u.id) || [],
                                                        });
                                                    }}
                                                >
                                                    <Icon icon="lucide:edit" />
                                                </button>

                                                <button
                                                    className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    data-bs-toggle="modal"
                                                    data-bs-target="#deleteModal"
                                                    onClick={() => setDeleteId(item.id)}
                                                >
                                                    <Icon icon="fluent:delete-24-regular" />
                                                </button>

                                            </div>

                                        </td>

                                    </tr>
                                ))}

                            </tbody>

                        </table>

                    </div>

                    {/* PAGINATION */}
                    <PaginationHelper
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        totalItems={total}
                        pageSize={entries}
                    />
                </div>
            </div>

            {/* CREATE / EDIT MODAL */}
            <div className="modal fade" id="helpSosModal">
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content radius-16">

                        <div className="modal-header">
                            <h5>{editId ? "Edit SOS" : "Create SOS"}</h5>
                            <button className="btn-close" data-bs-dismiss="modal" />
                        </div>

                        <div className="modal-body">

                            {/* TITLE */}
                            <div className="mb-3">
                                <label>
                                    Title <span className="text-danger">*</span>
                                </label>
                                <input
                                    className="form-control"
                                    maxLength={255}
                                    value={formData.title}
                                    onChange={(e) =>
                                        setFormData({ ...formData, title: e.target.value })
                                    }
                                />
                            </div>

                            {/* DESCRIPTION */}
                            <div className="mb-3">
                                <label>
                                    Description <small className="fst-italic">(Max 255 chars)</small>
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={4}
                                    maxLength={255}
                                    value={formData.description}
                                    onChange={(e) =>
                                        setFormData({ ...formData, description: e.target.value })
                                    }
                                />
                            </div>

                            {/* USERS */}
                            <div className="mb-3">
                                <label>
                                    Assign Users <span className="text-danger">*</span>
                                </label>

                                <Select
                                    isMulti
                                    options={userOptions}
                                    value={userOptions.filter((u) =>
                                        formData.userIds.includes(u.value)
                                    )}
                                    onChange={(selected) =>
                                        setFormData({
                                            ...formData,
                                            userIds: selected ? selected.map((s) => s.value) : [],
                                        })
                                    }
                                    placeholder="Select users"
                                />
                            </div>

                            <div className="text-end mt-3">
                                <button className="btn btn-primary" onClick={handleSubmit}>
                                    {editId ? "Update" : "Create"}
                                </button>
                            </div>

                        </div>

                    </div>
                </div>
            </div>

            {/* DELETE MODAL */}
            <div className="modal fade" id="deleteModal">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content text-center p-3">

                        <p>Are you sure you want to delete this SOS?</p>

                        <div className="d-flex justify-content-center gap-2">

                            <button className="btn btn-light" data-bs-dismiss="modal">
                                Cancel
                            </button>

                            <button
                                className="btn btn-danger"
                                onClick={() => {
                                    dispatch(deleteHelpSos(deleteId))
                                        .unwrap()
                                        .then((res) => {
                                            toast.success(res?.message || "Deleted successfully");

                                            dispatch(getHelpSos({
                                                page,
                                                limit: entries,
                                                search: debouncedSearch
                                            }));

                                            document.querySelector("#deleteModal .btn-close")?.click();
                                        })
                                        .catch((err) => {
                                            toast.error(
                                                err?.response?.data?.message ||
                                                err?.message ||
                                                "Delete failed"
                                            );
                                        });
                                }}
                            >
                                Delete
                            </button>

                        </div>

                    </div>
                </div>
            </div>

        </>
    );
};

export default HelpSosLayer;